import {NinchatAttributes} from 'api/types'
import {NinchatStoreUser} from 'api/types/store'
import React from 'react'
import {UserProps} from './types/types'
import UserView from './UserView'
import {getProtectedEmail} from 'utils/email'
import {useTranslation} from 'react-i18next'
import {useUsers} from 'hooks/ninchat'

export const getUserName = (props: {
    attrs: NinchatAttributes.NinchatUserAttributes | undefined
    fallbackName: string | undefined
    deletedUserName: string
    unnamedUserName: string
}): string => {
    const {
        attrs, fallbackName, deletedUserName, unnamedUserName,
    } = props
    if (!attrs?.name && fallbackName) {
        return fallbackName
    }
    // eslint-disable-next-line implicit-arrow-linebreak, no-extra-parens
    return (attrs && (attrs.name ? attrs.name : attrs.deleted ? deletedUserName : unnamedUserName)) || unnamedUserName
}

export const maybeWithProtectedEmail = (props: {
    user: NinchatStoreUser | undefined
    displayProtectedEmail: boolean
    deletedUserName: string
    unnamedUserName: string
    fallbackName?: string
}): string => {
    const {
        user,
        displayProtectedEmail,
        deletedUserName,
        unnamedUserName,
        fallbackName,
    } = props
    const userAttrs = user?.user_attrs
    const baseName = getUserName({
        attrs: userAttrs,
        deletedUserName,
        fallbackName,
        unnamedUserName,
    })

    if (displayProtectedEmail) {
        const email = getProtectedEmail(user?.user_identities)
        if (email) {
            return `${baseName} (${email})`
        }
    }

    return baseName
}

export const resolveUserDisplayName = (props: {
    t: (key: string) => string
    user: NinchatStoreUser | undefined
    displayProtectedEmail?: boolean
    fallbackName?: string
}): string => {
    const {
        t,
        user,
        displayProtectedEmail = false,
        fallbackName,
    } = props

    return maybeWithProtectedEmail({
        deletedUserName: t('Deleted user'),
        displayProtectedEmail,
        fallbackName,
        unnamedUserName: t('Unnamed user'),
        user,
    })
}

const User: React.FC<UserProps> = (props) => {
    const {t} = useTranslation()
    const {
        avatarIconOverride,
        avatarOnly,
        nameOnly,
        fallbackName,
        forceLetterAvatar,
        nameOverride,
        size,
        userId,
        displayProtectedEmail = false,
        displayRealName = false,
    } = props

    const users = useUsers([userId])
    const userName = nameOverride || resolveUserDisplayName({
        displayProtectedEmail,
        fallbackName,
        t,
        user: users[userId],
    })
    const connected = users[userId]?.user_attrs.connected ?? false
    const idle = Boolean(users[userId]?.user_attrs.idle)
    const iconURL = users[userId]?.user_attrs?.iconurl
    const realName = displayRealName ? users[userId]?.user_attrs.realname : undefined

    return (
        <UserView
            avatarOnly={avatarOnly}
            iconOverride={avatarIconOverride}
            iconURL={forceLetterAvatar ? undefined : iconURL}
            isConnected={connected}
            isIdle={idle}
            nameOnly={nameOnly}
            realName={realName}
            size={size}
            userName={userName}
        />
    )
}


export default User
